import dayjs from 'dayjs';
import { errorTypes, actionTypes } from '@/types';
import { logger } from '@/tools/logger';

// List available languages, they must also be imported in the fakeLoading function below.
const languageIsoToLocale = {
	en: 'en-gb',
	de: 'de',
	fr: 'fr',
};

export async function setDayjsLocale(_languageIso, dispatch) {
	logger.log(`setDayjsLocale to ${_languageIso}`);
	if (!_languageIso) return;
	// Webpack requires the `''+string` syntax otherwise the dynamic import does not work.
	if (_languageIso.length === 3) {
		_languageIso = _languageIso.slice(0, 2);
	}
	const languageIso = languageIsoToLocale[_languageIso] || _languageIso;
	logger.log(`language translated to ${languageIso}`);
	try {
		await import(`dayjs/locale/${languageIso}.js`);
	} catch (error) {
		dispatch(actionTypes.sendErrorMessage, { error: new Error(errorTypes.dateLocalizationNotAvailable) });
	}
	dayjs.locale(languageIso);
}

// In this function we pretend that we will load the locale
// libs from dayjs at some point in time. This triggers
// webpack to create cunk packages.
// We have to do this to so we can later dynamically load the
// locale file that we need with
// ```
// await import(`dayjs/locale/${language}`);
// ```
// This command can not be detected from webpack and would not
// create the chunck package. Therefore we have the code below.
/* eslint-disable no-unused-vars */
async function fakeLoading() {
	// Pretend that this could exist at some point in time.
	if (window.thisDoesNotExist0) {
		await import(/* webpackChunkName: "dayjs/locale/af" */ 'dayjs/locale/af.js');
		await import(/* webpackChunkName: "dayjs/locale/ar-dz" */ 'dayjs/locale/ar-dz.js');
		await import(/* webpackChunkName: "dayjs/locale/ar-kw" */ 'dayjs/locale/ar-kw.js');
		await import(/* webpackChunkName: "dayjs/locale/ar-ly" */ 'dayjs/locale/ar-ly.js');
		await import(/* webpackChunkName: "dayjs/locale/ar-ma" */ 'dayjs/locale/ar-ma.js');
		await import(/* webpackChunkName: "dayjs/locale/ar-sa" */ 'dayjs/locale/ar-sa.js');
		await import(/* webpackChunkName: "dayjs/locale/ar-tn" */ 'dayjs/locale/ar-tn.js');
		await import(/* webpackChunkName: "dayjs/locale/ar" */ 'dayjs/locale/ar.js');
		await import(/* webpackChunkName: "dayjs/locale/az" */ 'dayjs/locale/az.js');
		await import(/* webpackChunkName: "dayjs/locale/be" */ 'dayjs/locale/be.js');
		await import(/* webpackChunkName: "dayjs/locale/bg" */ 'dayjs/locale/bg.js');
		await import(/* webpackChunkName: "dayjs/locale/bm" */ 'dayjs/locale/bm.js');
		await import(/* webpackChunkName: "dayjs/locale/bn" */ 'dayjs/locale/bn.js');
		await import(/* webpackChunkName: "dayjs/locale/bo" */ 'dayjs/locale/bo.js');
		await import(/* webpackChunkName: "dayjs/locale/br" */ 'dayjs/locale/br.js');
		await import(/* webpackChunkName: "dayjs/locale/bs" */ 'dayjs/locale/bs.js');
		await import(/* webpackChunkName: "dayjs/locale/ca" */ 'dayjs/locale/ca.js');
		await import(/* webpackChunkName: "dayjs/locale/cs" */ 'dayjs/locale/cs.js');
		await import(/* webpackChunkName: "dayjs/locale/cv" */ 'dayjs/locale/cv.js');
		await import(/* webpackChunkName: "dayjs/locale/cy" */ 'dayjs/locale/cy.js');
		await import(/* webpackChunkName: "dayjs/locale/da" */ 'dayjs/locale/da.js');
		await import(/* webpackChunkName: "dayjs/locale/de-at" */ 'dayjs/locale/de-at.js');
		await import(/* webpackChunkName: "dayjs/locale/de-ch" */ 'dayjs/locale/de-ch.js');
		await import(/* webpackChunkName: "dayjs/locale/de" */ 'dayjs/locale/de.js');
		await import(/* webpackChunkName: "dayjs/locale/dv" */ 'dayjs/locale/dv.js');
		await import(/* webpackChunkName: "dayjs/locale/el" */ 'dayjs/locale/el.js');
		await import(/* webpackChunkName: "dayjs/locale/en-au" */ 'dayjs/locale/en-au.js');
		await import(/* webpackChunkName: "dayjs/locale/en-ca" */ 'dayjs/locale/en-ca.js');
		await import(/* webpackChunkName: "dayjs/locale/en-gb" */ 'dayjs/locale/en-gb.js');
		await import(/* webpackChunkName: "dayjs/locale/en-ie" */ 'dayjs/locale/en-ie.js');
		await import(/* webpackChunkName: "dayjs/locale/en-il" */ 'dayjs/locale/en-il.js');
		await import(/* webpackChunkName: "dayjs/locale/en-nz" */ 'dayjs/locale/en-nz.js');
		await import(/* webpackChunkName: "dayjs/locale/en-SG" */ 'dayjs/locale/en-SG.js');
		await import(/* webpackChunkName: "dayjs/locale/en" */ 'dayjs/locale/en.js');
		await import(/* webpackChunkName: "dayjs/locale/eo" */ 'dayjs/locale/eo.js');
		await import(/* webpackChunkName: "dayjs/locale/es-do" */ 'dayjs/locale/es-do.js');
		await import(/* webpackChunkName: "dayjs/locale/es-us" */ 'dayjs/locale/es-us.js');
		await import(/* webpackChunkName: "dayjs/locale/es" */ 'dayjs/locale/es.js');
		await import(/* webpackChunkName: "dayjs/locale/et" */ 'dayjs/locale/et.js');
		await import(/* webpackChunkName: "dayjs/locale/eu" */ 'dayjs/locale/eu.js');
		await import(/* webpackChunkName: "dayjs/locale/fa" */ 'dayjs/locale/fa.js');
		await import(/* webpackChunkName: "dayjs/locale/fi" */ 'dayjs/locale/fi.js');
		await import(/* webpackChunkName: "dayjs/locale/fo" */ 'dayjs/locale/fo.js');
		await import(/* webpackChunkName: "dayjs/locale/fr-ca" */ 'dayjs/locale/fr-ca.js');
		await import(/* webpackChunkName: "dayjs/locale/fr-ch" */ 'dayjs/locale/fr-ch.js');
		await import(/* webpackChunkName: "dayjs/locale/fr" */ 'dayjs/locale/fr.js');
		await import(/* webpackChunkName: "dayjs/locale/fy" */ 'dayjs/locale/fy.js');
		await import(/* webpackChunkName: "dayjs/locale/ga" */ 'dayjs/locale/ga.js');
		await import(/* webpackChunkName: "dayjs/locale/gd" */ 'dayjs/locale/gd.js');
		await import(/* webpackChunkName: "dayjs/locale/gl" */ 'dayjs/locale/gl.js');
		await import(/* webpackChunkName: "dayjs/locale/gom-latn" */ 'dayjs/locale/gom-latn.js');
		await import(/* webpackChunkName: "dayjs/locale/gu" */ 'dayjs/locale/gu.js');
		await import(/* webpackChunkName: "dayjs/locale/he" */ 'dayjs/locale/he.js');
		await import(/* webpackChunkName: "dayjs/locale/hi" */ 'dayjs/locale/hi.js');
		await import(/* webpackChunkName: "dayjs/locale/hr" */ 'dayjs/locale/hr.js');
		await import(/* webpackChunkName: "dayjs/locale/hu" */ 'dayjs/locale/hu.js');
		await import(/* webpackChunkName: "dayjs/locale/hy-am" */ 'dayjs/locale/hy-am.js');
		await import(/* webpackChunkName: "dayjs/locale/id" */ 'dayjs/locale/id.js');
		await import(/* webpackChunkName: "dayjs/locale/is" */ 'dayjs/locale/is.js');
		await import(/* webpackChunkName: "dayjs/locale/it-ch" */ 'dayjs/locale/it-ch.js');
		await import(/* webpackChunkName: "dayjs/locale/it" */ 'dayjs/locale/it.js');
		await import(/* webpackChunkName: "dayjs/locale/ja" */ 'dayjs/locale/ja.js');
		await import(/* webpackChunkName: "dayjs/locale/jv" */ 'dayjs/locale/jv.js');
		await import(/* webpackChunkName: "dayjs/locale/ka" */ 'dayjs/locale/ka.js');
		await import(/* webpackChunkName: "dayjs/locale/kk" */ 'dayjs/locale/kk.js');
		await import(/* webpackChunkName: "dayjs/locale/km" */ 'dayjs/locale/km.js');
		await import(/* webpackChunkName: "dayjs/locale/kn" */ 'dayjs/locale/kn.js');
		await import(/* webpackChunkName: "dayjs/locale/ko" */ 'dayjs/locale/ko.js');
		await import(/* webpackChunkName: "dayjs/locale/ku" */ 'dayjs/locale/ku.js');
		await import(/* webpackChunkName: "dayjs/locale/ky" */ 'dayjs/locale/ky.js');
		await import(/* webpackChunkName: "dayjs/locale/lb" */ 'dayjs/locale/lb.js');
		await import(/* webpackChunkName: "dayjs/locale/lo" */ 'dayjs/locale/lo.js');
		await import(/* webpackChunkName: "dayjs/locale/lt" */ 'dayjs/locale/lt.js');
		await import(/* webpackChunkName: "dayjs/locale/lv" */ 'dayjs/locale/lv.js');
		await import(/* webpackChunkName: "dayjs/locale/me" */ 'dayjs/locale/me.js');
		await import(/* webpackChunkName: "dayjs/locale/mi" */ 'dayjs/locale/mi.js');
		await import(/* webpackChunkName: "dayjs/locale/mk" */ 'dayjs/locale/mk.js');
		await import(/* webpackChunkName: "dayjs/locale/ml" */ 'dayjs/locale/ml.js');
		await import(/* webpackChunkName: "dayjs/locale/mn" */ 'dayjs/locale/mn.js');
		await import(/* webpackChunkName: "dayjs/locale/mr" */ 'dayjs/locale/mr.js');
		await import(/* webpackChunkName: "dayjs/locale/ms-my" */ 'dayjs/locale/ms-my.js');
		await import(/* webpackChunkName: "dayjs/locale/ms" */ 'dayjs/locale/ms.js');
		await import(/* webpackChunkName: "dayjs/locale/mt" */ 'dayjs/locale/mt.js');
		await import(/* webpackChunkName: "dayjs/locale/my" */ 'dayjs/locale/my.js');
		await import(/* webpackChunkName: "dayjs/locale/nb" */ 'dayjs/locale/nb.js');
		await import(/* webpackChunkName: "dayjs/locale/ne" */ 'dayjs/locale/ne.js');
		await import(/* webpackChunkName: "dayjs/locale/nl-be" */ 'dayjs/locale/nl-be.js');
		await import(/* webpackChunkName: "dayjs/locale/nl" */ 'dayjs/locale/nl.js');
		await import(/* webpackChunkName: "dayjs/locale/nn" */ 'dayjs/locale/nn.js');
		await import(/* webpackChunkName: "dayjs/locale/oc-lnc" */ 'dayjs/locale/oc-lnc.js');
		await import(/* webpackChunkName: "dayjs/locale/pa-in" */ 'dayjs/locale/pa-in.js');
		await import(/* webpackChunkName: "dayjs/locale/pl" */ 'dayjs/locale/pl.js');
		await import(/* webpackChunkName: "dayjs/locale/pt-br" */ 'dayjs/locale/pt-br.js');
		await import(/* webpackChunkName: "dayjs/locale/pt" */ 'dayjs/locale/pt.js');
		await import(/* webpackChunkName: "dayjs/locale/ro" */ 'dayjs/locale/ro.js');
		await import(/* webpackChunkName: "dayjs/locale/ru" */ 'dayjs/locale/ru.js');
		await import(/* webpackChunkName: "dayjs/locale/sd" */ 'dayjs/locale/sd.js');
		await import(/* webpackChunkName: "dayjs/locale/se" */ 'dayjs/locale/se.js');
		await import(/* webpackChunkName: "dayjs/locale/si" */ 'dayjs/locale/si.js');
		await import(/* webpackChunkName: "dayjs/locale/sk" */ 'dayjs/locale/sk.js');
		await import(/* webpackChunkName: "dayjs/locale/sl" */ 'dayjs/locale/sl.js');
		await import(/* webpackChunkName: "dayjs/locale/sq" */ 'dayjs/locale/sq.js');
		await import(/* webpackChunkName: "dayjs/locale/sr-cyrl" */ 'dayjs/locale/sr-cyrl.js');
		await import(/* webpackChunkName: "dayjs/locale/sr" */ 'dayjs/locale/sr.js');
		await import(/* webpackChunkName: "dayjs/locale/ss" */ 'dayjs/locale/ss.js');
		await import(/* webpackChunkName: "dayjs/locale/sv" */ 'dayjs/locale/sv.js');
		await import(/* webpackChunkName: "dayjs/locale/sw" */ 'dayjs/locale/sw.js');
		await import(/* webpackChunkName: "dayjs/locale/ta" */ 'dayjs/locale/ta.js');
		await import(/* webpackChunkName: "dayjs/locale/te" */ 'dayjs/locale/te.js');
		await import(/* webpackChunkName: "dayjs/locale/tet" */ 'dayjs/locale/tet.js');
		await import(/* webpackChunkName: "dayjs/locale/tg" */ 'dayjs/locale/tg.js');
		await import(/* webpackChunkName: "dayjs/locale/th" */ 'dayjs/locale/th.js');
		await import(/* webpackChunkName: "dayjs/locale/tl-ph" */ 'dayjs/locale/tl-ph.js');
		await import(/* webpackChunkName: "dayjs/locale/tlh" */ 'dayjs/locale/tlh.js');
		await import(/* webpackChunkName: "dayjs/locale/tr" */ 'dayjs/locale/tr.js');
		await import(/* webpackChunkName: "dayjs/locale/tzl" */ 'dayjs/locale/tzl.js');
		await import(/* webpackChunkName: "dayjs/locale/tzm-latn" */ 'dayjs/locale/tzm-latn.js');
		await import(/* webpackChunkName: "dayjs/locale/tzm" */ 'dayjs/locale/tzm.js');
		await import(/* webpackChunkName: "dayjs/locale/ug-cn" */ 'dayjs/locale/ug-cn.js');
		await import(/* webpackChunkName: "dayjs/locale/uk" */ 'dayjs/locale/uk.js');
		await import(/* webpackChunkName: "dayjs/locale/ur" */ 'dayjs/locale/ur.js');
		await import(/* webpackChunkName: "dayjs/locale/uz-latn" */ 'dayjs/locale/uz-latn.js');
		await import(/* webpackChunkName: "dayjs/locale/uz" */ 'dayjs/locale/uz.js');
		await import(/* webpackChunkName: "dayjs/locale/vi" */ 'dayjs/locale/vi.js');
		await import(/* webpackChunkName: "dayjs/locale/x-pseudo" */ 'dayjs/locale/x-pseudo.js');
		await import(/* webpackChunkName: "dayjs/locale/yo" */ 'dayjs/locale/yo.js');
		await import(/* webpackChunkName: "dayjs/locale/zh-cn" */ 'dayjs/locale/zh-cn.js');
		await import(/* webpackChunkName: "dayjs/locale/zh-hk" */ 'dayjs/locale/zh-hk.js');
		await import(/* webpackChunkName: "dayjs/locale/zh-tw" */ 'dayjs/locale/zh-tw.js');
	}
	// if (window.thisDoesNotExist1)
}
